import type {
  TFactorStateDTO,
  TStaffDirectoryDTO,
  TStaffMemberDTO,
  TStaffPermissionDTO,
  TStaffProfileDTO,
  TStaffTransitionReportDTO,
  TTransitionBlockerDTO,
  TTransitionCandidateDTO,
  TTransitionHoldDTO,
  TTransitionMovedDTO,
} from './staff.dto';

import type {
  TStaffAvailability,
  TStaffAwayReason,
  TStaffDirectoryEntry,
  TStaffFactor,
  TStaffHoldKind,
  TStaffMember,
  TStaffPermission,
  TStaffProfile,
  TStaffTransitionBlocker,
  TStaffTransitionCandidate,
  TStaffTransitionCandidates,
  TStaffTransitionHold,
  TStaffTransitionMove,
  TStaffTransitionReport,
} from './staff.types';

import { AVAILABILITIES, AWAY_REASONS, STAFF_HOLD_KINDS } from './staff.fields';

const toStaffProfile = (dto: TStaffProfileDTO): TStaffProfile => ({
  userID: dto.id,
  firstName: dto.first_name,
  lastName: dto.last_name,
  email: dto.email ?? '',
});

const toStaffMember = (dto: TStaffMemberDTO): TStaffMember => ({
  userID: dto.id,
  name: `${dto.first_name} ${dto.last_name}`.trim(),
});

const toStaffAvailability = (value: string | undefined): TStaffAvailability => AVAILABILITIES.find(key => key === value) ?? 'active';

const toStaffAwayReason = (value: string | null | undefined): TStaffAwayReason | null => AWAY_REASONS.find(key => key === value) ?? null;

const toStaffDirectoryEntry = (dto: TStaffDirectoryDTO): TStaffDirectoryEntry => ({
  userID: dto.id,
  firstName: dto.first_name ?? '',
  lastName: dto.last_name ?? '',
  email: dto.email ?? '',
  subRole: dto.staff?.sub_role ?? null,
  availability: toStaffAvailability(dto.staff?.availability),
  awayReason: toStaffAwayReason(dto.staff?.away_reason),
  awayUntil: dto.staff?.away_until ?? null,
  lastActiveAt: dto.staff?.last_active_at ?? null,
  hasCalendar: Boolean(dto.staff?.calendly_event_type_uri),
  calendarURI: dto.staff?.calendly_event_type_uri ?? null,
  hasSessionCalendar: Boolean(dto.staff?.calendly_session_event_type_uri),
  sessionCalendarURI: dto.staff?.calendly_session_event_type_uri ?? null,
  createdAt: dto.staff?.created_at ?? null,
});

const toStaffFactor = (dto: TFactorStateDTO): TStaffFactor => ({
  userID: dto.user_id,
  totp: dto.totp,
});

const toStaffPermission = (dto: TStaffPermissionDTO): TStaffPermission => ({
  action: dto.action,
  subRole: dto.sub_role,
});

const toStaffHoldKind = (value: string | undefined): TStaffHoldKind | null => STAFF_HOLD_KINDS.find(kind => kind === value) ?? null;

const toTransitionHolds = (dtos: readonly TTransitionHoldDTO[]): TStaffTransitionHold[] => dtos.flatMap((dto) => {
  const kind = toStaffHoldKind(dto.kind);

  if (kind === null) {
    return [];
  }

  return [{ kind, count: dto.count, isMoving: dto.disposition === 'move' }];
});

const toTransitionBlocker = (dto: TTransitionBlockerDTO): TStaffTransitionBlocker => ({
  code: dto.code,
  kind: toStaffHoldKind(dto.kind),
  receiverID: dto.staff_id ?? null,
  bookingIDs: dto.ids ?? [],
});

const toTransitionCandidate = (dto: TTransitionCandidateDTO): TStaffTransitionCandidate => ({
  staffID: dto.staff_id,
  subRole: dto.sub_role,
});

const toTransitionCandidates = (dto: Record<string, TTransitionCandidateDTO[]>): TStaffTransitionCandidates => ({
  applicant: (dto.applicant ?? []).map(toTransitionCandidate),
  member: (dto.member ?? []).map(toTransitionCandidate),
});

const toTransitionMoves = (dtos: readonly TTransitionMovedDTO[]): TStaffTransitionMove[] => dtos.flatMap((dto) => {
  const kind = toStaffHoldKind(dto.kind);

  if (kind === null) {
    return [];
  }

  return [{ kind, staffID: dto.staff_id, count: dto.count }];
});

const toStaffTransitionReport = (dto: TStaffTransitionReportDTO): TStaffTransitionReport => ({
  holds: toTransitionHolds(dto.holds ?? []),
  blockers: (dto.blockers ?? []).map(toTransitionBlocker),
  candidates: toTransitionCandidates(dto.candidates ?? {}),
  moved: toTransitionMoves(dto.moved ?? []),
  isApplied: dto.applied,
});

export {
  toStaffDirectoryEntry,
  toStaffFactor,
  toStaffMember,
  toStaffPermission,
  toStaffProfile,
  toStaffTransitionReport,
};
